/**
 * locale.ts — Resolves which locale a component should render in.
 *
 * Resolution order (highest priority first):
 *   1. Explicit `locale`/`lang` on config, or a `lang`/`locale` attribute on the
 *      element — deterministic and testable.
 *   2. The closest ancestor `lang` attribute (walks up through shadow roots),
 *      falling back to `<html lang>` — the web-standard signal.
 *   3. A leading URL path segment (e.g. `/fr/...`) when it names a known locale.
 *   4. `navigator.language` — the visitor's browser preference.
 *   5. The default locale ('en').
 *
 * The result is normalized to a bundled base language (e.g. 'fr-CA' → 'fr') by
 * the i18n service; only values that resolve to a bundled locale are honoured so
 * an unknown token doesn't blank the UI.
 */

import { i18n } from './i18n-service.js';

interface LocaleConfig {
    locale?: string;
    lang?: string;
    [key: string]: unknown;
}

/** First non-empty candidate that maps to a bundled locale wins. */
export function resolveLocale(el: HTMLElement, config: LocaleConfig = {}): string {
    for (const candidate of localeCandidates(el, config)) {
        if (candidate && i18n.hasLocale(candidate)) {
            return i18n.normalize(candidate);
        }
    }
    return 'en';
}

function* localeCandidates(el: HTMLElement, config: LocaleConfig): Generator<string> {
    // 1. Explicit config / attribute override.
    yield String(config.locale ?? config.lang ?? '');
    yield el.getAttribute('lang') ?? '';
    yield el.getAttribute('locale') ?? '';

    // 2. Closest ancestor lang (pierces shadow boundaries), then <html lang>.
    yield closestLang(el);
    yield document.documentElement.lang;

    // 3. Leading URL path segment (e.g. /fr/people).
    yield firstPathSegment();

    // 4. Browser preference.
    yield navigator.language ?? '';
}

/** Walk up the DOM (through shadow roots) for the nearest `lang` attribute. */
function closestLang(start: HTMLElement): string {
    let node: Node | null = start;
    while (node) {
        if (node instanceof HTMLElement) {
            const lang = node.getAttribute('lang');
            if (lang) return lang;
        }
        const parent: Node | null =
            (node as HTMLElement).parentElement ??
            (node.getRootNode() instanceof ShadowRoot ? (node.getRootNode() as ShadowRoot).host : null);
        if (parent === node) break;
        node = parent;
    }
    return '';
}

/** The first non-empty segment of the current URL path, if any. */
function firstPathSegment(): string {
    const segment = window.location.pathname.split('/').find((s) => s.trim());
    return segment ?? '';
}
